/**
 * bugReport.js — REP-10. The ONLY producer of a patient's problem-report payload, of the
 * screen facts that ride with it, and of the order in which the report and its files go out.
 *
 * Pure: no React, no imports. scripts/check-report-problem.mjs imports it under plain node.
 *
 * The server half is orella-api `support_submit_ticket_patient` (POST /support/tickets/patient)
 * and `support_patient_ticket_attachment` (POST /support/tickets/patient/<ticket_id>/attachments),
 * D7 branch, NOT live as of v242.251.0 — which is why every caller sits behind
 * REPORT_PROBLEM_ENABLED (src/config/featureFlags.js).
 */

/**
 * ★★★ COPIED IN SHAPE FROM App.jsx's <Route> TABLE, NOT IN VALUE FROM window.location.
 *   scripts/check-report-problem.mjs (arm R1) re-reads App.jsx and requires the two lists to be
 *   equal IN BOTH DIRECTIONS, so a route added to the app without being added here goes red
 *   instead of silently becoming UNKNOWN_ROUTE on every report filed from it.
 * ★★ /sign-agreement/:token and /join/:inviteCode carry a secret in the URL, and /case/:caseId,
 *   /specialty/:specialtyId and /doctor/:doctorId carry ids. Only the TEMPLATE ever leaves the
 *   device; the segment is never read, so it cannot be sent.
 */
export const BUG_REPORT_ROUTES = Object.freeze([
  '/', '/landing', '/sign-agreement/:token',
  '/splash', '/welcome', '/signup', '/signin', '/email', '/verify', '/security', '/2fa-upgrade',
  '/forgot-password', '/forgot-password/verify', '/forgot-password/new-password',
  '/forgot-password/email', '/reset-password', '/two-factor-reset', '/join', '/join/:inviteCode',
  '/onboarding', '/ai-consent/renew',
  '/home', '/specialties', '/specialty/:specialtyId',
  '/case/emergency', '/case/home',
  '/case/describe/urgent', '/case/describe/voice', '/case/describe/details',
  '/case/describe/signs-symptoms', '/case/describe/context', '/case/describe/additional',
  '/case/describe/adaptive', '/case/describe/review', '/case/describe/summary',
  '/case/describe/conditions', '/case/describe/warnings',
  '/case/encounters', '/case/note', '/case/authorizations', '/case/agreement', '/case/detect',
  '/case/diagnose', '/case/schedule-followup', '/case/deliver',
  '/case/deliver/medication-survey', '/case/deliver/further-testing', '/case/deliver/instructions',
  '/case/share-choice', '/case/share', '/case/share-hub', '/case/find-doctor', '/case/send-report', '/case/referral-request', '/case/referral-upload', '/case/:caseId', '/case/:caseId/allow-access', '/cases', '/visits',
  '/profile', '/profile/identification', '/profile/insurance', '/profile/allergies',
  '/profile/medications', '/profile/medical-history', '/profile/family-history',
  '/profile/social-lifestyle', '/profile/import-records',
  '/settings', '/notifications', '/notification-preferences', '/settings/change-password',
  '/settings/two-factor', '/settings/security', '/settings/session-pin',
  '/settings/delete-account', '/settings/ai-consent', '/settings/email', '/settings/phone',
  '/doctors', '/doctors/mine', '/doctor/:doctorId',
  '/settings/privacy-policy', '/settings/terms',
])

export const UNKNOWN_ROUTE = 'unrecognised route'

/**
 * ★★★ AN ALLOWLIST, NOT A REDACTION. The return value is one of the literal strings in
 *   BUG_REPORT_ROUTES or UNKNOWN_ROUTE — a path SEGMENT is never carried forward, so an id
 *   cannot leak whatever shape it has. Literal routes win over parametric ones:
 *   '/case/home' is '/case/home', never '/case/:caseId'.
 * ★★ Only the pathname passed in is read. Nothing else from location (no full URL, no query,
 *   no fragment) is touched anywhere in this file.
 */
export function routeNameFor(pathname) {
  // No pathname is "we could not find out", never the root route.
  if (typeof pathname !== 'string' || !pathname.startsWith('/')) return UNKNOWN_ROUTE
  const segs = pathname.split('/').filter(Boolean)
  const parts = (pattern) => pattern.split('/').filter(Boolean)
  for (const pattern of BUG_REPORT_ROUTES) {
    const p = parts(pattern)
    if (p.length === segs.length && p.every((s, i) => s === segs[i])) return pattern
  }
  for (const pattern of BUG_REPORT_ROUTES) {
    const p = parts(pattern)
    if (p.length !== segs.length || !p.some((s) => s.charAt(0) === ':')) continue
    if (p.every((s, i) => s.charAt(0) === ':' || s === segs[i])) return pattern
  }
  return UNKNOWN_ROUTE
}

/**
 * ★★★ A COPY OF orella-api's _SUPPORT_ROUTE_PATTERN, ON PURPOSE. The server validates
 *   route_pattern positively and writes `route=<refused>` for anything else — its alarm that a
 *   client sends resolved URLs. The app must never send a value it knows will be refused, so a
 *   template that fails this shape ('/2fa-upgrade' starts with a digit) is OMITTED, never sent.
 *   check-report-problem.mjs (arm R3) compares this source with the API's when the API sibling is
 *   readable, and pins the routes it rejects either way.
 */
export const SERVER_ROUTE_PATTERN_SOURCE =
  '^(?:/|(?:/(?:[a-z][a-z-]{0,23}|:[A-Za-z][A-Za-z0-9]{0,30}))+/?)$'
const SERVER_ROUTE_SHAPE = new RegExp(SERVER_ROUTE_PATTERN_SOURCE)
export const WIRE_OMITTED = Object.freeze(['/2fa-upgrade'])

/** The template that may go on the wire for a template, or null (then the key is omitted). */
export function wirePattern(pattern) {
  if (!pattern || pattern === UNKNOWN_ROUTE) return null
  if (!BUG_REPORT_ROUTES.includes(pattern)) return null
  if (WIRE_OMITTED.includes(pattern)) return null
  return SERVER_ROUTE_SHAPE.test(pattern) ? pattern : null
}

export function wireRoutePattern(pathname) {
  return wirePattern(routeNameFor(pathname))
}

/** Settings screens are where the report is written, not what it is about (PA-3). */
export function isSettingsPattern(pattern) {
  return typeof pattern === 'string' && (pattern === '/settings' || pattern.startsWith('/settings/'))
}

/**
 * ★★ THE WIRE VALUES ARE THE WEBSITE'S EIGHT, IN ENGLISH, IN EVERY LANGUAGE. orella-api
 *   `_SUPPORT_PATIENT_CATEGORIES` refuses anything else (a translated value would be a ninth
 *   category nobody filters on). The app translates the LABEL (labelKey) and sends `value`.
 */
export const PATIENT_SUPPORT_CATEGORIES = Object.freeze([
  { value: 'Login / Account access', labelKey: 'support.categories.loginAccount' },
  { value: 'Case submission error', labelKey: 'support.categories.caseSubmission' },
  { value: 'Notification issue', labelKey: 'support.categories.notification' },
  { value: 'Case status question', labelKey: 'support.categories.caseStatus' },
  { value: 'After-visit summary access', labelKey: 'support.categories.afterVisitSummary' },
  { value: 'Data privacy / HIPAA access request', labelKey: 'support.categories.privacyAccess' },
  { value: 'App bug', labelKey: 'support.categories.appBug' },
  { value: 'Other', labelKey: 'support.categories.other' },
])
export const CATEGORY_VALUES = Object.freeze(PATIENT_SUPPORT_CATEGORIES.map((c) => c.value))
/** Steps and expected-vs-actual are asked only where they mean something. */
export const BUG_CATEGORIES = Object.freeze(['App bug', 'Case submission error'])
export const CRASH_CATEGORY = 'App bug'

/** orella-api `_SUPPORT_PATIENT_ORIGINS`. */
export const SUPPORT_ORIGINS = Object.freeze(['settings', 'crash'])

/** orella-api `_SUPPORT_PATIENT_MESSAGE_MAX`: longer is refused (message_too_long), never cut. */
export const MESSAGE_MAX = 5000
/** The server CUTS steps/expected at 2000. The form stops at 2000 so nothing typed is lost. */
export const DETAIL_MAX = 2000

/** orella-api `_SUPPORT_ATTACH_*`. Checked here as a courtesy; the server is the authority. */
export const ATTACH_MAX_FILES = 3
export const ATTACH_MAX_BYTES = 2 * 1024 * 1024
export const ATTACH_MAX_TOTAL = 4 * 1024 * 1024
const ATTACH_EXT_ALIASES = Object.freeze({ jpeg: 'jpg', heif: 'heic' })
const ATTACH_EXTS = Object.freeze(['jpg', 'png', 'webp', 'heic', 'pdf'])
const ATTACH_TYPE_EXT = Object.freeze({
  'image/jpeg': 'jpg', 'image/jpg': 'jpg', 'image/png': 'png', 'image/webp': 'webp',
  'image/heic': 'heic', 'image/heif': 'heic', 'application/pdf': 'pdf',
})
export const ATTACH_ACCEPT =
  '.jpg,.jpeg,.png,.webp,.heic,.heif,.pdf,image/jpeg,image/png,image/webp,image/heic,image/heif,application/pdf'

/** The server's extension for a file (from its name, else its declared type), or null. */
export function attachmentExt(file) {
  const name = String((file && file.name) || '')
  const dot = name.lastIndexOf('.')
  if (dot > 0) {
    let ext = name.slice(dot + 1).toLowerCase()
    ext = ATTACH_EXT_ALIASES[ext] || ext
    if (ATTACH_EXTS.includes(ext)) return ext
  }
  const type = String((file && file.type) || '').split(';')[0].trim().toLowerCase()
  return ATTACH_TYPE_EXT[type] || null
}

/**
 * Why `file` cannot join `list` ('type' | 'empty' | 'size' | 'count' | 'total'), or null.
 * `list` holds the files already admitted.
 */
export function attachmentRefusal(list, file) {
  const held = Array.isArray(list) ? list : []
  if (!attachmentExt(file)) return 'type'
  const size = Number((file && file.size) || 0)
  if (size <= 0) return 'empty'
  if (size > ATTACH_MAX_BYTES) return 'size'
  if (held.length >= ATTACH_MAX_FILES) return 'count'
  const total = held.reduce((n, f) => n + Number((f && f.size) || 0), 0)
  if (total + size > ATTACH_MAX_TOTAL) return 'total'
  return null
}

/**
 * ★★ THE NAME THE SERVER SEES IS OURS, NOT THE PATIENT'S. A file name is free text the
 *   patient (or her phone) chose — "lab results Maria 1984.pdf" — and the server needs only
 *   its extension. So the upload is named attachment-<n>.<ext>; the original name never
 *   leaves the device. The server chooses the storage path itself either way.
 */
export function attachmentUploadName(index, file) {
  return `attachment-${index + 1}.${attachmentExt(file)}`
}

/**
 * The scalar facts the server's allowlist reads (viewport, appVersion, locale, timeZone)
 * plus route_pattern when a wire-safe template exists. The User-Agent travels as a header.
 * `pattern` is a TEMPLATE from routeNameFor, never a pathname.
 */
export function buildBrowserFacts({ win, language, appVersion, pattern } = {}) {
  const w = win || (typeof window !== 'undefined' ? window : null)
  let tz = 'unknown'
  try { tz = Intl.DateTimeFormat().resolvedOptions().timeZone || 'unknown' } catch (_) { tz = 'unknown' }
  const facts = {
    viewport: w ? `${w.innerWidth || 0}x${w.innerHeight || 0}` : 'unknown',
    appVersion: String(appVersion || 'unknown'),
    locale: String(language || 'unknown'),
    timeZone: tz,
  }
  const route = wirePattern(pattern)
  if (route) facts.route_pattern = route
  return facts
}

/**
 * ★★ THE LINE UNDER THE FORM IS RENDERED FROM THE SAME OBJECT THAT IS SENT, so the two cannot
 *   drift. Values only — no labels — so there is nothing in it to translate.
 */
export function describeBrowserFacts(facts, userAgent) {
  const f = facts || {}
  return [f.route_pattern, f.appVersion, f.locale, f.timeZone, f.viewport, userAgent ? String(userAgent).slice(0, 300) : null]
    .filter(Boolean)
    .join(' · ')
}

/**
 * ★★★ NO IDENTITY, AND THE KEY SET IS THE CONTRACT. orella-api takes name, e-mail and user id
 *   from the token's users row and ignores the body's version; sending them would be a second,
 *   weaker truth. So the payload is exactly:
 *     category, message, origin, viewport, appVersion, locale, timeZone
 *   plus route_pattern when there is a wire-safe template, plus steps_to_reproduce and
 *   expected_vs_actual only for BUG_CATEGORIES and only when typed. check-report-problem.mjs
 *   (arm C) asserts the set EXHAUSTIVELY, so adding a key here goes red.
 */
export function buildTicketPayload({ category, message, steps, expected, facts, origin } = {}) {
  const f = facts || {}
  const payload = {
    category: String(category || ''),
    message: String(message || '').trim(),
    origin: SUPPORT_ORIGINS.includes(origin) ? origin : 'settings',
    viewport: f.viewport || 'unknown',
    appVersion: f.appVersion || 'unknown',
    locale: f.locale || 'unknown',
    timeZone: f.timeZone || 'unknown',
  }
  if (f.route_pattern) payload.route_pattern = f.route_pattern
  if (BUG_CATEGORIES.includes(payload.category)) {
    const s = String(steps || '').trim()
    const e = String(expected || '').trim()
    if (s) payload.steps_to_reproduce = s
    if (e) payload.expected_vs_actual = e
  }
  return payload
}

/** The i18n key of the first thing wrong with the form, or null. Mirrors the server's 400s. */
export function reportFormProblem({ category, message, steps, expected } = {}) {
  if (!CATEGORY_VALUES.includes(category)) return 'support.errCategory'
  const m = String(message || '').trim()
  if (!m) return 'support.errMessageRequired'
  if (m.length > MESSAGE_MAX) return 'support.errMessageTooLong'
  if (String(steps || '').length > DETAIL_MAX || String(expected || '').length > DETAIL_MAX) {
    return 'support.errDetailTooLong'
  }
  return null
}

/** The i18n key for a refused ticket POST (the contract's status codes). */
export function ticketErrorKey(err) {
  const status = err && typeof err.status === 'number' ? err.status : 0
  const code = err && err.code
  if (status === 400) {
    if (code === 'message_too_long') return 'support.errMessageTooLong'
    if (code === 'message_invalid') return 'support.errFailed'
    return 'support.errCheckForm'
  }
  if (status === 401) return 'support.errSession'
  if (status === 403) return 'support.errForbidden'
  if (status === 404) return 'support.errUnavailable'
  if (status === 429) return 'support.errRateLimited'
  if (status === 503) return 'support.errAccount'
  if (status === 0 || status === 408) return 'support.errNetwork'
  return 'support.errFailed'
}

/** The i18n key for a refused attachment POST. */
export function attachmentErrorKey(err) {
  const status = err && typeof err.status === 'number' ? err.status : 0
  if (status === 409) return 'support.attachErrWindow'
  if (status === 413) return 'support.attachErrLimit'
  if (status === 415) return 'support.attachErrType'
  if (status === 404) return 'support.attachErrUnavailable'
  if (status === 401 || status === 403) return 'support.attachErrSession'
  if (status === 429) return 'support.attachErrRateLimited'
  return 'support.attachErrFailed'
}

/**
 * ★★★ THE ORDER: THE REPORT FIRST, THEN ONE FILE PER CALL. The attachment route needs the
 *   ticket id the 201 answers, and it takes files only while the ticket is open and at most 30
 *   minutes old. No upload starts unless the ticket POST resolved; a refused ticket uploads
 *   nothing. A file that fails after a filed report is reported as exactly that ('partial'),
 *   never as a clean success and never as a failed report.
 *
 * `api` = { submitPatientTicket(payload), uploadPatientAttachment(ticketId, file, uploadName) }.
 * `files` = File/Blob objects already admitted by attachmentRefusal.
 *
 * Returns one of
 *   { kind: 'error', key, status, code }                      nothing was filed
 *   { kind: 'sent', ticketNumber, attached }                  filed, every file attached
 *   { kind: 'partial', ticketNumber, failed, total, keys }    filed, `failed` files not attached
 * ★ Nothing here logs: the message is free text a patient typed.
 */
export async function fileReport({ api, payload, files } = {}) {
  const list = Array.isArray(files) ? files : []
  let res
  try {
    res = await api.submitPatientTicket(payload)
  } catch (err) {
    return {
      kind: 'error',
      key: ticketErrorKey(err),
      status: err && typeof err.status === 'number' ? err.status : 0,
      code: (err && err.code) || null,
    }
  }
  const ticketNumber = (res && res.ticket_number) || null
  const ticketId = (res && res.ticket_id) || null
  if (!list.length) return { kind: 'sent', ticketNumber, attached: 0 }
  if (!ticketId) {
    return { kind: 'partial', ticketNumber, failed: list.length, total: list.length, keys: ['support.attachErrFailed'] }
  }
  const keys = []
  let failed = 0
  for (let i = 0; i < list.length; i++) {
    try {
      await api.uploadPatientAttachment(ticketId, list[i], attachmentUploadName(i, list[i]))
    } catch (err) {
      failed++
      const key = attachmentErrorKey(err)
      if (!keys.includes(key)) keys.push(key)
    }
  }
  if (failed) return { kind: 'partial', ticketNumber, failed, total: list.length, keys }
  return { kind: 'sent', ticketNumber, attached: list.length }
}
